const base64Url = (bytes: ArrayBuffer | Uint8Array) => {
  const arr = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  let str = '';
  for (let i = 0; i < arr.length; i++) str += String.fromCharCode(arr[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const generateCodeVerifier = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
};

export const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const data = new TextEncoder().encode(verifier);
  if (crypto?.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64Url(digest);
  }
  // fallback для незащищённого контекста (HTTP)
  return sha256Fallback(data).then(base64Url);
};

/**
 * Резервная реализация SHA-256 на чистом JS (по спецификации RFC 6234).
 *
 * Используется только когда `crypto.subtle` недоступен - это происходит
 * в незащищённом контексте (HTTP на не-localhost адресе, например http://10.x.x.x).
 * В продакшене (HTTPS) и на localhost всегда используется нативный `crypto.subtle`.
 */
const sha256Fallback = (data: Uint8Array): Promise<Uint8Array> => {
  return new Promise((resolve) => {
    // Pure-JS SHA-256 (RFC 6234)
    const K = [
      0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
      0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
      0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
      0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
      0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
      0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
      0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
      0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
    ];
    let h = [0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19];
    const r = (x: number, n: number) => (x >>> n) | (x << (32 - n));
    const len = data.length;
    const bitLen = len * 8;
    const padLen = len % 64 < 56 ? 56 - (len % 64) : 120 - (len % 64);
    const msg = new Uint8Array(len + padLen + 8);
    msg.set(data);
    msg[len] = 0x80;
    for (let i = 0; i < 8; i++) msg[msg.length - 1 - i] = (bitLen / Math.pow(2, i * 8)) & 0xff;
    for (let i = 0; i < msg.length; i += 64) {
      const w = new Array(64).fill(0);
      for (let j = 0; j < 16; j++) w[j] = (msg[i+j*4]<<24)|(msg[i+j*4+1]<<16)|(msg[i+j*4+2]<<8)|msg[i+j*4+3];
      for (let j = 16; j < 64; j++) {
        const s0 = r(w[j-15],7)^r(w[j-15],18)^(w[j-15]>>>3);
        const s1 = r(w[j-2],17)^r(w[j-2],19)^(w[j-2]>>>10);
        w[j] = (w[j-16]+s0+w[j-7]+s1) >>> 0;
      }
      let [a,b,c,d,e,f,g,hh] = h;
      for (let j = 0; j < 64; j++) {
        const S1 = r(e,6)^r(e,11)^r(e,25);
        const ch = (e&f)^(~e&g);
        const t1 = (hh+S1+ch+K[j]+w[j]) >>> 0;
        const S0 = r(a,2)^r(a,13)^r(a,22);
        const maj = (a&b)^(a&c)^(b&c);
        const t2 = (S0+maj) >>> 0;
        hh=g; g=f; f=e; e=(d+t1)>>>0; d=c; c=b; b=a; a=(t1+t2)>>>0;
      }
      h = h.map((v,i) => (v + [a,b,c,d,e,f,g,hh][i]) >>> 0);
    }
    const out = new Uint8Array(32);
    h.forEach((v, i) => { out[i*4]=(v>>>24)&0xff; out[i*4+1]=(v>>>16)&0xff; out[i*4+2]=(v>>>8)&0xff; out[i*4+3]=v&0xff; });
    resolve(out);
  });
};

const KEY = 'pkce_verifier';

export const saveVerifier = (v: string) => sessionStorage.setItem(KEY, v);
export const loadVerifier = () => sessionStorage.getItem(KEY);
export const clearVerifier = () => sessionStorage.removeItem(KEY);
